import { useState, useEffect } from 'react';
import { useProductPlans } from '@/hooks/useProductPlans';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ProductPlanWithProduct } from '@/types/productPlans';
import type { Product } from '@/contexts/ProductContext';

interface ProductPlanFormProps {
  open: boolean;
  onClose: () => void;
  plan: ProductPlanWithProduct | null;
  products: Product[];
}

export function ProductPlanForm({ open, onClose, plan, products }: ProductPlanFormProps) {
  const { createPlan, updatePlan } = useProductPlans();
  
  const [formData, setFormData] = useState({
    product_id: '',
    name: '',
    recurrence_type: '' as any,
    currency: 'BRL' as 'BRL' | 'USD' | 'EUR',
    price: '',
    gateway_plan_id: '',
  });

  useEffect(() => {
    if (plan) {
      setFormData({
        product_id: plan.product_id,
        name: plan.name,
        recurrence_type: plan.recurrence_type,
        currency: plan.currency,
        price: plan.price.toFixed(2).replace('.', ','),
        gateway_plan_id: plan.gateway_plan_id || '',
      });
    } else {
      setFormData({
        product_id: '',
        name: '',
        recurrence_type: '',
        currency: 'BRL',
        price: '',
        gateway_plan_id: '',
      });
    }
  }, [plan, open]);

  const handlePriceChange = (value: string) => {
    // Remove tudo exceto números e vírgula
    const cleaned = value.replace(/[^\d,]/g, '');
    
    // Se vazio ou zero, permitir
    if (cleaned === '' || cleaned === '0' || cleaned === '0,') {
      setFormData({ ...formData, price: cleaned });
      return;
    }
    
    // Converter para formato correto
    const numbers = cleaned.replace(/[^\d]/g, '');
    const amount = parseFloat(numbers) / 100;
    setFormData({ ...formData, price: amount.toFixed(2).replace('.', ',') });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Permitir 0,00 para planos gratuitos
    const priceValue = formData.price === '' || formData.price === '0' || formData.price === '0,' || formData.price === '0,0' || formData.price === '0,00'
      ? 0
      : parseFloat(formData.price.replace(',', '.'));
    
    const planData = {
      product_id: formData.product_id,
      name: formData.name,
      recurrence_type: formData.recurrence_type,
      currency: formData.currency,
      price: priceValue,
      gateway_plan_id: formData.gateway_plan_id || null,
      is_active: true,
    };

    if (plan) {
      await updatePlan.mutateAsync({ id: plan.id, updates: planData });
    } else {
      await createPlan.mutateAsync(planData as any);
    }
    
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {plan ? 'Editar Plano' : 'Novo Plano'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="product">Produto *</Label>
            <Select
              value={formData.product_id}
              onValueChange={(value) => setFormData({ ...formData, product_id: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o produto" />
              </SelectTrigger>
              <SelectContent>
                {products.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Nome do Plano *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Plano Básico"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="recurrence">Recorrência *</Label>
            <Select
              value={formData.recurrence_type}
              onValueChange={(value: any) => setFormData({ ...formData, recurrence_type: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Diário</SelectItem>
                <SelectItem value="weekly">Semanal</SelectItem>
                <SelectItem value="monthly">Mensal</SelectItem>
                <SelectItem value="bimonthly">Bimestral</SelectItem>
                <SelectItem value="quarterly">Trimestral</SelectItem>
                <SelectItem value="semiannual">Semestral</SelectItem>
                <SelectItem value="annual">Anual</SelectItem>
                <SelectItem value="lifetime">Vitalício</SelectItem>
                <SelectItem value="custom">Customizada</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currency">Moeda *</Label>
              <Select
                value={formData.currency}
                onValueChange={(value: any) => setFormData({ ...formData, currency: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BRL">BRL (R$)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">Valor</Label>
              <Input
                id="price"
                value={formData.price}
                onChange={(e) => handlePriceChange(e.target.value)}
                placeholder="0,00"
              />
              <p className="text-xs text-muted-foreground">
                Digite 0,00 para plano gratuito
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gateway_id">ID Gateway</Label>
            <Input
              id="gateway_id"
              value={formData.gateway_plan_id}
              onChange={(e) => setFormData({ ...formData, gateway_plan_id: e.target.value })}
              placeholder="ID do plano no gateway (opcional)"
            />
            <p className="text-xs text-muted-foreground">
              Identificador do plano no gateway de pagamento
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createPlan.isPending || updatePlan.isPending}>
              {plan ? 'Salvar' : 'Criar Plano'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
